import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function GlAccountActionsPopover({ glAccount, onEditClick }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState(null);
  const [toggleOpen, setToggleOpen] = useState(false);
  const isOpen = Boolean(anchorEl);
  const isActive = glAccount?.isActive;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () =>
      // TODO: replace with real GL toggle service
      Promise.resolve({
        message: `GL account ${isActive ? "deactivated" : "activated"} successfully`,
      }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["gl-accounts"] });
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const handleToggleClick = () => {
    handleClose();
    setToggleOpen(true);
  };

  const handleDialogClose = () => {
    setToggleOpen(false);
    toggleMutation.reset();
  };

  const handleView = () => {
    handleClose();
    navigate(`/gl-management/accounts/${glAccount.id}`);
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { elevation: 2, sx: { borderRadius: "8px", mt: 0.5 } },
        }}
      >
        <PopoverContent>
          <StyledMenuItem onClick={handleView}>View GL Account</StyledMenuItem>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditClick(glAccount);
            }}
          >
            Edit GL Account
          </StyledMenuItem>
          <Divider sx={{ my: 0.5 }} />

          {isActive ? (
            <StyledMenuItem onClick={handleToggleClick} danger>
              Deactivate Account
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleToggleClick}>
              Activate Account
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={toggleOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate GL Account" : "Activate GL Account"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${glAccount?.accountName ?? "this GL account"}?`
            : `Are you sure you want to activate ${glAccount?.accountName ?? "this GL account"}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
